import { useEffect, useState } from 'preact/hooks'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'wouter-preact'
import { listConversations, type InboxStats } from '../api/endpoints'
import { Banner, EmptyState, SiteBadge, StatusPill } from '../components/ui'
import { timeAgo } from '../utils/time'
import type { ConversationListItem } from '../types'
import { ConversationDetailPage } from './conversation-detail'

/**
 * Inbox wrapper — Gmail-style split pane on desktop (list left, the open
 * conversation beside it, no navigation transitions); phone keeps the
 * list⇄detail flow unchanged. Both routes render this: /conversations and
 * /conversations/:id differ only in whether a detail is open.
 */
export function InboxPage({ id }: { id?: string }) {
  return (
    <div class="inbox-split">
      <div class={`inbox-pane-list${id ? ' has-detail' : ''}`}>
        <ConversationsPage selectedId={id} />
      </div>
      <div class={`inbox-pane-detail${id ? '' : ' is-empty'}`}>
        {id ? (
          <ConversationDetailPage id={id} key={id} />
        ) : (
          <div class="inbox-pane-placeholder">
            <div style={{ fontSize: 28, opacity: 0.35 }}>💬</div>
            <div>Select a conversation</div>
          </div>
        )}
      </div>
    </div>
  )
}

// Collapsed-state memory for the KPI strip — module scope, so it survives
// SPA route changes. An installed PWA almost never hard-reloads, so "until
// reload" would mean "never again": instead the strip re-greets when the
// app returns to the foreground after 4+ hours away, and a manual toggle
// in the subtitle row reopens it any time (pinned — scroll won't re-hide
// a strip the operator deliberately opened).
let kpiDismissedAt: number | null = null
let kpiManualPin = false
const KPI_REGREET_MS = 4 * 60 * 60 * 1000

function KpiStrip({ stats }: { stats: InboxStats }) {
  const items: Array<{ k: string; v: string; m: string; bar: string }> = [
    {
      k: 'Conversations',
      v: String(stats.conversations),
      m: 'last 7 days',
      bar: 'linear-gradient(90deg, var(--m-primary, #1e3a8a), var(--m-accent, #14b8a6))',
    },
    {
      k: 'Resolved by Monty',
      v: stats.resolvedPct === null ? '—' : `${stats.resolvedPct}%`,
      m: 'no human needed',
      bar: '#10b981',
    },
    {
      k: 'Leads captured',
      v: String(stats.leads),
      m: 'phone or email taken',
      bar: '#14b8a6',
    },
    {
      k: 'Out of hours',
      v: stats.outOfHours === null ? '—' : String(stats.outOfHours),
      m:
        stats.outOfHours === null
          ? 'set business hours to track'
          : 'answered while you were closed',
      bar: 'var(--m-primary, #1e3a8a)',
    },
  ]
  return (
    <div class="kpi-strip">
      {items.map((it) => (
        <div class="kpi-card" key={it.k}>
          <span class="kpi-k">{it.k}</span>
          <span class="kpi-v">{it.v}</span>
          <div class="kpi-rule" style={{ background: it.bar }} />
          <span class="kpi-m">{it.m}</span>
        </div>
      ))}
    </div>
  )
}

type InboxFilter = 'active' | 'closed' | 'all'

const FILTER_LABELS: Record<InboxFilter, string> = {
  active: 'Active',
  closed: 'Closed',
  all: 'All',
}

export function ConversationsPage({ selectedId }: { selectedId?: string } = {}) {
  const [filter, setFilter] = useState<InboxFilter>('active')
  const [kpisCollapsed, setKpisCollapsed] = useState(
    kpiDismissedAt !== null && Date.now() - kpiDismissedAt < KPI_REGREET_MS
  )

  // Collapse the KPI strip on the first meaningful scroll-down of the list
  // (window on phones, the list pane on desktop). Scrolling the open
  // conversation doesn't count — reading a thread shouldn't shift the list.
  useEffect(() => {
    if (kpisCollapsed) return
    const onScroll = (e: Event) => {
      if (kpiManualPin) return
      const t = e.target as Document | HTMLElement
      if (t instanceof HTMLElement && t.closest('.inbox-pane-detail')) return
      const top =
        t instanceof HTMLElement
          ? t.scrollTop
          : window.scrollY || document.documentElement.scrollTop
      if (top > 80) {
        kpiDismissedAt = Date.now()
        setKpisCollapsed(true)
      }
    }
    document.addEventListener('scroll', onScroll, true)
    return () => document.removeEventListener('scroll', onScroll, true)
  }, [kpisCollapsed])

  // Re-greet: app foregrounded after a long gap → show the strip again.
  useEffect(() => {
    const onVisible = () => {
      if (
        document.visibilityState === 'visible' &&
        kpiDismissedAt !== null &&
        Date.now() - kpiDismissedAt >= KPI_REGREET_MS
      ) {
        kpiDismissedAt = null
        kpiManualPin = false
        setKpisCollapsed(false)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const toggleKpis = () => {
    if (kpisCollapsed) {
      kpiDismissedAt = null
      kpiManualPin = true
      setKpisCollapsed(false)
    } else {
      kpiManualPin = false
      kpiDismissedAt = Date.now()
      setKpisCollapsed(true)
    }
  }
  const [search, setSearch] = useState('')
  // Debounced copy drives the query so we don't fire per keystroke.
  const [query, setQuery] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['conversations', filter, query],
    queryFn: ({ signal }) =>
      listConversations({ signal, status: query ? 'all' : filter, q: query || undefined }),
    // Keep showing the previous results while a new search/filter loads —
    // avoids the full-page spinner flashing between keystrokes.
    placeholderData: keepPreviousData,
    refetchInterval: (q) =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
        ? false
        : 8_000,
  })

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span class="spinner spinner-dark" />
      </div>
    )
  }
  if (isError) {
    return (
      <>
        <Banner variant="error">
          Couldn’t load conversations. <a onClick={() => refetch()}>Retry</a>
        </Banner>
      </>
    )
  }

  const rows = data?.conversations ?? []
  const counts = data?.counts
  const stats = data?.stats
  const needsReply = counts?.needsReply ?? rows.filter((r) => r.status === 'escalated').length

  const resolvedBit =
    stats && stats.resolvedPct !== null ? ` · ${stats.resolvedPct}% resolved by Monty` : ''
  const summary =
    rows.length === 0
      ? filter === 'active'
        ? 'No active conversations.'
        : filter === 'closed'
          ? 'No closed conversations.'
          : 'No conversations yet.'
      : `${needsReply} need${needsReply === 1 ? 's' : ''} reply${resolvedBit} · ${counts?.all ?? rows.length} total`

  const emptySubtitle =
    filter === 'active'
      ? 'New escalations will appear here and send a push notification.'
      : filter === 'closed'
        ? 'Conversations move here automatically 30 min after the last reply.'
        : 'No bot or operator conversations on record yet.'

  return (
    <>
      <div style={{ marginBottom: '0.9rem' }}>
        <h1 style={{ marginBottom: 4, fontSize: 23, letterSpacing: '-0.015em' }}>Inbox</h1>
        <p style={{ color: 'var(--m-text-muted)', fontSize: 'var(--m-text-sm)', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span>{summary}</span>
          {stats && (
            <button type="button" class="kpi-toggle" onClick={toggleKpis}>
              {kpisCollapsed ? '▸ Show stats' : '▾ Hide stats'}
            </button>
          )}
        </p>
      </div>

      {stats && (
        <div class={`kpi-wrap${kpisCollapsed ? ' kpi-collapsed' : ''}`}>
          <KpiStrip stats={stats} />
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <input
          type="search"
          value={search}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          placeholder="Search name, email, or message text…"
          aria-label="Search conversations"
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
        />
      </div>

      <div class="inbox-tabs" role="tablist" aria-label="Inbox filter">
        {(Object.keys(FILTER_LABELS) as InboxFilter[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            class={`inbox-tab${filter === key ? ' inbox-tab-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {FILTER_LABELS[key]}
            {counts && (
              <em class="inbox-tab-count">
                {key === 'active' ? counts.active : key === 'closed' ? counts.closed : counts.all}
              </em>
            )}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState title="You’re all clear" subtitle={emptySubtitle} />
      ) : (
        <ul class="conv-list conv-panel">
          {rows.map((c) => (
            <ConversationRow key={c.id} conv={c} selected={c.id === selectedId} />
          ))}
        </ul>
      )}
    </>
  )
}

// Warm, distinct avatar colours — deterministic per visitor so the same
// person keeps their colour across visits and pages.
const AVATAR_COLOURS = [
  '#0e7490', '#7c3aed', '#be185d', '#b45309',
  '#15803d', '#1d4ed8', '#a21caf', '#c2410c',
]

function Avatar({ label }: { label: string }) {
  const words = label.trim().split(/\s+/)
  const initials =
    words.length >= 2
      ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
      : label.slice(0, 2).toUpperCase()
  let hash = 0
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0
  const colour = AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length]
  return (
    <span
      aria-hidden="true"
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 700,
        letterSpacing: '.02em',
        color: '#fff',
        background: colour,
      }}
    >
      {initials}
    </span>
  )
}

function ConversationRow({ conv, selected }: { conv: ConversationListItem; selected?: boolean }) {
  const rowClass =
    conv.status === 'escalated'
      ? 'conv-needs-reply'
      : conv.status === 'timed_out'
        ? 'conv-timed-out'
        : conv.status === 'human'
          ? 'conv-human'
          : conv.status === 'closed'
            ? 'conv-closed'
            : ''
  const when = conv.escalatedAt ?? conv.createdAt
  const visitor = conv.visitorName || conv.visitorEmail || 'Anonymous visitor'
  const preview = conv.escalationSummary ?? conv.lastMessage ?? 'New enquiry'
  // "Lead" means real contact intent — a phone number, or an email on a
  // conversation that reached the team. The email-gate address alone would
  // put the chip on nearly every row and mean nothing.
  const hasLead = Boolean(conv.visitorPhone || (conv.visitorEmail && conv.escalatedAt))
  return (
    <li>
      <Link href={`/conversations/${conv.id}`} class={`conv-card ${rowClass}${selected ? ' conv-selected' : ''}`}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Avatar label={visitor} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div class="conv-row">
              <span class="conv-title">
                {conv.isOnline && (
                  <span
                    class="conv-online-dot"
                    title="Visitor is on the page right now"
                    aria-label="Visitor is online"
                  />
                )}
                {visitor}
              </span>
              <span class="conv-time">{timeAgo(when)}</span>
            </div>
            <div class="conv-preview">{preview}</div>
            <div class="conv-meta">
              <SiteBadge site={conv.site} />
              <StatusPill status={conv.status} />
              {conv.handledBy && conv.status === 'human' && (
                <span style={{ fontSize: 12, color: 'var(--m-success, #15803d)', fontWeight: 600 }}>
                  ● {conv.handledBy}
                </span>
              )}
              {hasLead && <span class="conv-lead-chip">Lead ✓</span>}
            </div>
          </div>
        </div>
      </Link>
    </li>
  )
}
